import { Card, CardContent, Checkbox, cn, ScrollArea } from '@design-system';

import { ChevronDown } from 'lucide-react';

import { useRef } from 'react';
import { useScrollToBottomDetection } from '@/features/advisor-onboarding';

import { termsOfServiceDef } from '@shared';

interface AdvisorOnboardingConsentStepProps {
  hasConsentedToTos: boolean;
  hasConsentedToSms: boolean;
  onTosConsentChange: (checked: boolean) => void;
  onSmsConsentChange: (checked: boolean) => void;
  isSubmitting?: boolean;
  hasScrolledToBottom?: boolean;
  onScrolledToBottom?: () => void;
}

export function AdvisorOnboardingConsentStep({
  hasConsentedToTos,
  hasConsentedToSms,
  onTosConsentChange,
  onSmsConsentChange,
  isSubmitting = false,
  hasScrolledToBottom = false,
  onScrolledToBottom,
}: AdvisorOnboardingConsentStepProps) {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useScrollToBottomDetection({
    scrollAreaRef,
    onScrolledToBottom,
    hasScrolledToBottom,
  });

  const checkboxesDisabled = isSubmitting || !hasScrolledToBottom;

  return (
    <div className="flex flex-col items-center justify-center">
      <div className="w-full max-w-2xl space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-950">Terms of Service</h2>
          <p className="text-foreground mt-2">
            Please review and accept our{' '}
            <a
              href="https://www.waterlily.com/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:text-primary/80 underline"
            >
              Terms of Service
            </a>{' '}
            to continue.
          </p>
        </div>

        <Card className="relative overflow-hidden p-0">
          <CardContent className="p-0">
            <div ref={scrollAreaRef}>
              <ScrollArea className="h-64 w-full rounded-md md:h-80 lg:min-h-48 lg:h-[calc(100vh-480px)]">
                <div className="text-foreground p-4 text-sm leading-relaxed">
                  {termsOfServiceDef.text.split('\n').map((line, index) => {
                    // Skip empty lines but add spacing
                    if (line.trim() === '') {
                      return (
                        <div
                          key={index}
                          className="h-2"
                        />
                      );
                    }

                    // Section headers like "1. Acceptance of Terms"
                    const isSectionHeader = /^\d+\.\s/.test(line);
                    // Sub-headers like "Acknowledgement of Non-Responsibility:"
                    const isSubHeader = line.endsWith(':') && line.length < 50;
                    // Title lines
                    const isTitle = line === 'Terms of Services' || line.startsWith('Last Updated');

                    return (
                      <p
                        key={index}
                        className={cn(
                          'mb-1',
                          isSectionHeader && 'mt-2 font-semibold text-gray-950',
                          isSubHeader && 'text-foreground mt-2 font-medium',
                          isTitle && 'font-semibold text-gray-950',
                        )}
                      >
                        {line}
                      </p>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          </CardContent>

          {!hasScrolledToBottom && (
            <div className="bg-linear-to-t pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center justify-end rounded-b-md from-white via-white/80 to-transparent pb-3 pt-8">
              <div className="text-muted-foreground flex items-center gap-1 text-sm">
                <ChevronDown className="h-4 w-4 animate-bounce" />
                <span>Scroll to read the entire Terms of Service</span>
              </div>
            </div>
          )}
        </Card>

        <div className="space-y-4">
          <div
            className={cn('flex items-start gap-3', checkboxesDisabled && 'opacity-60')}
            title={
              !hasScrolledToBottom
                ? 'Please scroll through the entire Terms of Service first'
                : undefined
            }
          >
            <Checkbox
              id="tos-consent"
              checked={hasConsentedToTos}
              onCheckedChange={checked => onTosConsentChange(checked === true)}
              disabled={checkboxesDisabled}
              className="mt-0.5"
            />
            <label
              htmlFor="tos-consent"
              className={cn(
                'text-foreground text-sm',
                checkboxesDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              I have read, understood, and agree to the{' '}
              <a
                href="https://www.waterlily.com/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline"
              >
                Terms of Service
              </a>
              . I acknowledge that my use of Waterlily's services is subject to these terms.
            </label>
          </div>

          <div
            className={cn('flex items-start gap-3', checkboxesDisabled && 'opacity-60')}
            title={
              !hasScrolledToBottom
                ? 'Please scroll through the entire Terms of Service first'
                : undefined
            }
          >
            <Checkbox
              id="sms-consent"
              checked={hasConsentedToSms}
              onCheckedChange={checked => onSmsConsentChange(checked === true)}
              disabled={checkboxesDisabled}
              className="mt-0.5"
            />
            <label
              htmlFor="sms-consent"
              className={cn(
                'text-foreground text-sm',
                checkboxesDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
              )}
            >
              I agree to receive SMS messages to verify my identity during sign-in.
            </label>
          </div>
        </div>
      </div>
    </div>
  );
}
